<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import CloverFullLogo from "@/components/ui/images/CloverFullLogo.vue";
import WavesBg from "@/components/ui/images/WavesBg.vue";
import FormInput from "@/components/ui/forms/Input.vue";
import LoginPage from "./LoginPage.vue";
import PlanTypeCard from "./Checkout/PlanTypeCard.vue";
import LoadingOverlay from "@/components/_elements/LoadingOverlay.vue";
import { useBuyNow } from "./hooks";
import { getCsrfToken } from "@/api/utils.ts";
import { usePlanStore } from "@/stores/planStore";
import PasswordRules from "@/components/Auth/PasswordRules.vue";
import { useCheckoutStore } from "./Checkout/checkoutStore";
import CheckoutAlert from "@/components/Account/Checkout/CheckoutAlert.vue";

const buyNow = useBuyNow();
const planStore = usePlanStore();

const props = withDefaults(defineProps<{ errors: string }>(), {
    errors: "[]",
});
const serverErrors: { [key: string]: string[] } = JSON.parse(props.errors);
const isSubmitting = ref(false);
const passwordValid = ref(false);
const clientPasswordErrors = ref<string[]>([]);

const passwordErrors = computed(() => {
    if (clientPasswordErrors.value.length) {
        return clientPasswordErrors.value;
    }

    return serverErrors?.create_password;
});

const setSubmitting = () => {
    isSubmitting.value = true;
};

const registerData = ref({
    create_first_name: "",
    create_last_name: "",
    create_email: "",
    create_notifications: true,
    create_password: "",
    create_trial: !buyNow.value,
});

const getQsParams = () => window.location.search;
const survey = ref(false);
const showLogin = ref(false);

const handleFrequencyChange = async (frequency: string) => {
    try {
        await planStore.updateFrequency(frequency);
    } catch (error) {
        console.error("Failed to update frequency:", error);
    }
};

const checkoutStore = useCheckoutStore();

onMounted(async () => {
    await checkoutStore.applyCouponFromUrl();

    try {
        await planStore.initializePlanData();
    } catch (error) {
        console.error("Failed to initialize plan data:", error);
    }
});

const formRef = ref<HTMLFormElement>();

const submitForm = () => {
    if (formRef.value) {
        if (!passwordValid.value) {
            if (clientPasswordErrors.value.length === 0) {
                clientPasswordErrors.value = [
                    "Please meet the password requirements.",
                ];
            }
            return;
        }
        setSubmitting();
        formRef.value.submit();
    }
};
</script>
<template>
    <LoadingOverlay v-if="planStore.isLoading || isSubmitting" />
    <LoginPage v-if="showLogin" :isRegisterPage="true" errors="[]" />
    <div
        class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 relative pb-24 min-h-screen flex items-center"
        v-if="!survey && !showLogin"
    >
        <div
            class="flex flex-col md:flex-row items-center gap-10 md:gap-20 justify-between pt-8 md:pt-16 w-full"
        >
            <div class="w-full max-w-xl md:max-w-none space-y-10">
                <a
                    class="flex justify-start h-10 w-auto"
                    href="https://cloverlearning.com"
                >
                    <CloverFullLogo fullwidth />
                    <span class="sr-only">Clover Learning Home Page</span>
                </a>
                <div class="flex items-center gap-1">
                    <checkout-tab
                        label="Create Account"
                        :step="1"
                        state="active"
                    ></checkout-tab>
                    <checkout-tab
                        label="Payment Details"
                        :step="2"
                    ></checkout-tab>
                </div>
                <div>
                    <div class="text-left" v-if="!buyNow">
                        <h2
                            class="text-2xl md:text-4xl font-bold tracking-tight"
                        >
                            Try it free for 3 days!
                        </h2>
                        <p class="mt-2">
                            No credit card required. All the features of our
                            Student plan.
                        </p>
                    </div>
                    <div class="text-left space-y-1" v-if="buyNow">
                        <h2
                            class="text-2xl md:text-2xl font-bold tracking-tight"
                        >
                            Create an Account
                        </h2>
                        <p class="text-gray-500 text-base">
                            Unlock your full potential.
                        </p>
                    </div>
                    <div class="flex flex-col space-y-8 mt-6">
                        <form
                            ref="formRef"
                            :action="`/register${getQsParams()}`"
                            method="POST"
                            @submit="setSubmitting"
                        >
                            <input
                                type="hidden"
                                name="_token"
                                :value="getCsrfToken()"
                            />
                            <input
                                type="hidden"
                                name="create_notifications"
                                :value="registerData.create_notifications"
                            />
                            <div class="space-y-6">
                                <div class="grid md:grid-cols-2 gap-6">
                                    <FormInput
                                        type="text"
                                        id="create_first_name"
                                        name="create_first_name"
                                        label="First name"
                                        v-model="registerData.create_first_name"
                                        :error="serverErrors?.create_first_name"
                                    />
                                    <FormInput
                                        type="text"
                                        id="create_last_name"
                                        name="create_last_name"
                                        label="Last name"
                                        v-model="registerData.create_last_name"
                                        :error="serverErrors?.create_last_name"
                                    />
                                </div>
                                <div>
                                    <FormInput
                                        type="text"
                                        id="create_email"
                                        name="create_email"
                                        label="Email"
                                        v-model="registerData.create_email"
                                        :error="serverErrors?.create_email"
                                    />
                                </div>
                                <div>
                                    <FormInput
                                        id="create_password"
                                        name="create_password"
                                        label="Password"
                                        type="password"
                                        v-model="registerData.create_password"
                                        :error="passwordErrors"
                                    />
                                    <div class="mt-3">
                                        <PasswordRules
                                            :password="
                                                registerData.create_password
                                            "
                                            @update:valid="
                                                passwordValid = $event
                                            "
                                            @update:errors="
                                                clientPasswordErrors = $event
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="text-left text-sm">
                                    <p>
                                        <a class="cta" href="/redeem">
                                            Redeem access code </a
                                        >&nbsp;| Already have an account?
                                        <a
                                            href=""
                                            class="cta"
                                            @click.prevent="showLogin = true"
                                            >Sign In</a
                                        >
                                    </p>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <PlanTypeCard
                :plan="planStore.planData"
                :isLoading="planStore.isLoading"
                :error="planStore.error"
                :onRetry="() => planStore.fetchPlanData()"
                :hideTaxes="true"
                :hideTotal="true"
                buttonText="Proceed to Payment"
                v-model:notifications="registerData.create_notifications"
                @select-plan="submitForm"
                @frequency-change="handleFrequencyChange"
            />
        </div>
    </div>
    <div class="hidden md:block">
        <waves-bg position="right" />
    </div>
    <div class="md:hidden">
        <waves-bg position="bottom" />
    </div>
    <CheckoutAlert />
</template>
